import { createContext, useContext, type ReactNode } from 'react';

import type { CurrentUser } from '@/lib/permissions';

/**
 * Lightweight auth context. The actual data flow is owned by react-query
 * (`useMe`) — this context just lets nested components read the resolved
 * user without re-running the query manually.
 */
interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue>({ user: null, isLoading: false });

interface AuthProviderProps {
  user: CurrentUser | null;
  isLoading: boolean;
  children: ReactNode;
}

export const AuthProvider = ({ user, isLoading, children }: AuthProviderProps) => {
  return <AuthContext.Provider value={{ user, isLoading }}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export const useAuth = (): AuthContextValue => useContext(AuthContext);

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export const useCurrentUser = (): CurrentUser | null => useContext(AuthContext).user;
